<script setup lang="ts">
import InputOutputPlayground from "~/features/python/ch2/programs/components/InputOutputPlayground.vue";
import ProgramQuestionCard from "~/features/python/ch2/programs/components/ProgramQuestionCard.vue";
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { ch2Questions } from "~/features/python/ch2/exercise/data/ch2.questions";
import { pythonInteractiveCards } from "~/features/python/data/chapter2-interactive";

const programTopics = ["variables", "input-output", "operators", "loops", "functions"] as const;
const programQuestions = ch2Questions.filter(
  (question) => question.type !== "mcq" && programTopics.includes(question.topic),
);
const relatedCards = pythonInteractiveCards.filter((item) => item.to !== "/11th/computer/python/programs/");

usePageSeo({
  title: "Chapter 2 Python Programs and Output Questions | Ustaad",
  description: "Chapter 2 Python program practice with output questions, coding questions and board-style examples for class 11 students.",
  keywords: "chapter 2 python programs, python output questions class 11, 11th class computer python practical questions",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Ch 2 Python", item: "/11th/computer/python/" },
    { name: "Programs", item: "/11th/computer/python/programs/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "Chapter 2 Python Programs",
    items: [
      { name: "Chapter 2 Python", path: "/11th/computer/python/" },
      { name: "Chapter 2 Python Variables", path: "/11th/computer/python/variables/" },
      { name: "Chapter 2 Python Operators", path: "/11th/computer/python/operators/" },
      { name: "Chapter 2 Python Functions", path: "/11th/computer/python/functions/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="rounded-3xl bg-slate-950 p-6 text-white">
        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-sky-300">Python Chapter 2</p>
        <h1 class="mt-2 text-2xl font-bold tracking-tight">Chapter 2 Python Programs</h1>
        <p class="mt-3 text-sm leading-7 text-slate-200">
          Yahan aap dekhoge ke code likhne par screen par kya aata hai. Phir aap khud answer doge aur har sawal ko step by step samjhoge.
        </p>
      </section>

      <InputOutputPlayground />

      <section id="program-list" class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Program Practice</p>
          <h2 class="mt-2 text-lg font-semibold text-slate-900 dark:text-white">Yeh wale sawal try karo</h2>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">Jo sawal karna ho us par click karo. Neeche usi page par poora hal mil jayega.</p>
        </div>

        <div class="mt-5 grid gap-3">
          <a
            v-for="question in programQuestions"
            :key="question.id"
            :href="`#q-${question.id}`"
            class="rounded-2xl border border-slate-200 bg-slate-50 p-4 transition hover:border-sky-300 hover:bg-sky-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
          >
            <div class="flex flex-wrap items-center justify-between gap-3">
              <h3 class="text-sm font-semibold text-slate-900 dark:text-white">Q{{ question.number }}. {{ question.question }}</h3>
              <span class="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
                {{ question.type }} / {{ question.topic }}
              </span>
            </div>
          </a>
        </div>
      </section>

      <section class="space-y-8">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Full Practice</p>
          <h2 class="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">Ab sab sawal yahin karo</h2>
        </div>

        <ProgramQuestionCard
          v-for="(question, index) in programQuestions"
          :key="question.id"
          :question="question"
          :position="index + 1"
          :total="programQuestions.length"
        />
      </section>

      <section class="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">More Topics</p>
          <h2 class="mt-2 text-lg font-semibold text-slate-900 dark:text-white">More Python Topics</h2>
        </div>
        <div class="mt-4 grid gap-3 md:grid-cols-3">
          <NuxtLink
            v-for="item in relatedCards"
            :key="item.to"
            :to="item.to"
            class="rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-sky-300 hover:bg-sky-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-sky-700 dark:hover:bg-slate-800"
          >
            <h3 class="text-sm font-semibold text-slate-900 dark:text-white">{{ item.title }}</h3>
            <p class="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
          </NuxtLink>
        </div>
      </section>
    </div>
  </PageContainer>
</template>
